/**
 * KRA Photo Journal – multi-file Web Share helper.
 *
 * Bundles N image URLs into File objects and hands them to the OS share sheet
 * via `navigator.share({ files })`. On iOS Safari this surfaces the native
 * "Save N Images" → Photos action (same UX as WhatsApp's multi-image share).
 *
 * Falls back to sequential blob downloads on browsers that don't support
 * sharing files (desktop Chrome/Firefox).
 */

const IS_IOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

export interface ShareItem {
  url: string;
  /** Suggested file name (without extension — extension comes from blob MIME). */
  baseName: string;
}

export type ShareResult =
  | { kind: "shared"; count: number }
  | { kind: "downloaded"; count: number }
  | { kind: "cancelled" }
  | { kind: "error"; message: string };

function extFromMime(type: string): string {
  const t = type.toLowerCase();
  if (t.includes("webp")) return "webp";
  if (t.includes("png")) return "png";
  return "jpg";
}

async function fetchAsFile(item: ShareItem): Promise<File> {
  const resp = await fetch(item.url);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const blob = await resp.blob();
  const name = `${item.baseName}.${extFromMime(blob.type)}`;
  return new File([blob], name, { type: blob.type || "image/webp" });
}

/**
 * Share many image URLs via the OS share sheet. On supported mobile browsers
 * this opens the native sheet — picking "Save N Images" writes them straight
 * into the Photos library. On unsupported browsers it falls back to N
 * sequential blob downloads.
 */
export async function shareImages(
  items: ShareItem[],
  opts: { title?: string; onProgress?: (loaded: number, total: number) => void } = {},
): Promise<ShareResult> {
  if (items.length === 0) return { kind: "error", message: "Không có ảnh nào" };

  // Fetch all blobs in parallel (capped concurrency would be nicer, but
  // typical share batch is small enough that browser fetch queueing handles it)
  const files: File[] = [];
  let loaded = 0;
  try {
    await Promise.all(
      items.map(async (it) => {
        const f = await fetchAsFile(it);
        files.push(f);
        loaded += 1;
        opts.onProgress?.(loaded, items.length);
      }),
    );
  } catch (err) {
    return {
      kind: "error",
      message: err instanceof Error ? `Tải ảnh lỗi: ${err.message}` : "Tải ảnh lỗi",
    };
  }

  // ── Web Share API (mobile + macOS Safari 16.4+) ──────────────────────────
  const canUseShare =
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files });

  if (canUseShare) {
    try {
      await navigator.share({ files, title: opts.title });
      return { kind: "shared", count: files.length };
    } catch (err) {
      // User dismissed the share sheet — not an error worth shouting about
      if (err instanceof DOMException && err.name === "AbortError") {
        return { kind: "cancelled" };
      }
      // Some Android builds throw on share but the fall-through download still
      // works — let the user keep their files via the desktop fallback below.
      if (!IS_IOS) {
        // continue to fallback
      } else {
        return {
          kind: "error",
          message: err instanceof Error ? err.message : "Chia sẻ thất bại",
        };
      }
    }
  }

  // ── Fallback: sequential <a download> ────────────────────────────────────
  for (const f of files) {
    const url = URL.createObjectURL(f);
    const a = document.createElement("a");
    a.href = url;
    a.download = f.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    // tiny gap so browsers don't drop subsequent downloads
    await new Promise((r) => setTimeout(r, 120));
  }
  return { kind: "downloaded", count: files.length };
}
